import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { QuoteModal } from "@/components/QuoteModal";
import { Search, Ship, Calendar } from "lucide-react";
import { toast } from "sonner";

// Import Tracking sub-components
import { ShipmentOverview } from "@/components/tracking/ShipmentOverview";
import { ShipmentJourney } from "@/components/tracking/ShipmentJourney";
import { LiveShipmentMap } from "@/components/tracking/LiveShipmentMap";
import { ShipmentDetails } from "@/components/tracking/ShipmentDetails";
import { DocumentsList } from "@/components/tracking/DocumentsList";

const API_URL = "/api";

const Tracking = () => {
  const [searchParams] = useSearchParams();
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [trackingId, setTrackingId] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shipment, setShipment] = useState<any>(null);
  const [statusUpdates, setStatusUpdates] = useState<any[]>([]);

  useEffect(() => {
    const idFromUrl = searchParams.get("id");
    if (idFromUrl) {
      setTrackingId(idFromUrl);
      fetchShipmentData(idFromUrl);
    }
  }, [searchParams]);

  const fetchShipmentData = async (id: string) => {
    if (!id.trim()) {
      toast.error("Please enter a tracking number");
      return;
    }

    setLoading(true);
    setShowResults(false);

    try {
      // Fetch shipment data
      const shipmentResponse = await fetch(
        `${API_URL}/shipments.php?tracking_number=${id.trim()}`
      );
      const shipmentData = await shipmentResponse.json();

      if (!shipmentData.success) {
        toast.error("Tracking number not found");
        setLoading(false);
        return;
      }

      // Fetch status updates
      const updatesResponse = await fetch(
        `${API_URL}/status-updates.php?tracking_number=${id.trim()}`
      );
      const updatesData = await updatesResponse.json();

      setShipment(shipmentData.data);
      setStatusUpdates(updatesData.success ? updatesData.data : []);
      setShowResults(true);
      toast.success("Tracking information loaded");
    } catch (error) {
      console.error("Tracking error:", error);
      toast.error("Failed to fetch tracking information");
    } finally {
      setLoading(false);
    }
  };

  const handleTrack = async (e: React.FormEvent) => {
    e.preventDefault();
    fetchShipmentData(trackingId);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar onQuoteClick={() => setQuoteModalOpen(true)} />

      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4 max-w-6xl">
          {/* Header */}
          <div className="text-center mb-10 animate-fade-in">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-slate-800">Track Your Shipment</h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Enter your tracking number below to view real-time updates and live location of your cargo.
            </p>
          </div>

          {/* Search Form */}
          <form onSubmit={handleTrack} className="mb-8 animate-fade-in max-w-3xl mx-auto">
            <Card className="border-0 shadow-md">
              <CardContent className="p-4 md:p-6">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      placeholder="Enter Tracking No. (e.g., CM123456789)"
                      value={trackingId}
                      onChange={(e) => setTrackingId(e.target.value)}
                      className="pl-12 h-12 md:h-14 text-lg bg-slate-50 border-gray-200 focus:bg-white"
                      disabled={loading}
                    />
                  </div>
                  <Button type="submit" size="lg" className="h-12 md:h-14 px-8 text-lg w-full sm:w-auto" disabled={loading}>
                    {loading ? "Searching..." : "Track"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </form>

          {/* Tracking Results Dashboard */}
          {showResults && shipment && (
            <div className="space-y-5 animate-fade-in">
              {/* Row 1: Overview */}
              <ShipmentOverview shipment={shipment} />

              {/* Row 2: Journey & Map */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="lg:col-span-1">
                  <ShipmentJourney updates={statusUpdates} />
                </div>
                <div className="lg:col-span-2">
                  {/* Map disabled - Commented out temporarily */}
                  {/* <LiveShipmentMap shipment={shipment} updates={statusUpdates} /> */}
                  
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 flex flex-col items-center justify-center text-center h-full min-h-[350px] md:min-h-[400px]">
                    <div className="relative mb-6">
                      <span className="absolute top-0 right-0 flex h-4 w-4 -mt-1 -mr-1">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-4 w-4 bg-blue-500"></span>
                      </span>
                      <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center">
                        <Ship className="h-8 w-8 text-blue-600 animate-pulse" />
                      </div>
                    </div>
                    
                    <h3 className="font-bold text-2xl mb-2 text-slate-800">Live Status Information</h3>
                    <p className="text-slate-600 max-w-sm mb-6">
                      Live tracking map has been temporarily deactivated. Click below to view the latest active status coordinates and status updates.
                    </p>
                    
                    <Button 
                      size="lg"
                      className="bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md transition-all hover:scale-[1.02] px-8 py-6 rounded-xl text-base"
                      onClick={() => {
                        const latestUpdate = statusUpdates[0] || { 
                          location: shipment.origin, 
                          description: 'Shipment registered at source' 
                        };
                        toast.info(
                          <div className="flex flex-col gap-1.5 p-1 text-left">
                            <div className="flex items-center gap-2">
                              <span className="font-extrabold text-slate-900 text-sm">Status:</span>
                              <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800 font-bold">
                                {shipment.current_status}
                              </span>
                            </div>
                            <div>
                              <span className="font-bold text-slate-800 text-xs">Location:</span>{" "}
                              <span className="text-slate-600 text-xs">{latestUpdate.location}</span>
                            </div>
                            <div>
                              <span className="font-bold text-slate-800 text-xs">Details:</span>{" "}
                              <span className="text-slate-500 text-xs">{latestUpdate.description}</span>
                            </div>
                          </div>,
                          { 
                            duration: 6000,
                            position: "top-center"
                          }
                        );
                      }}
                    >
                      Show Current Status Info
                    </Button>
                  </div>
                </div>
              </div>

              {/* Row 3: Details & Documents */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div>
                  <ShipmentDetails shipment={shipment} />
                </div>
                <div>
                  <DocumentsList documents={shipment.documents} />
                </div>
              </div>
            </div>
          )}

          {/* Info Section (shown when no results) */}
          {!showResults && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16 max-w-4xl mx-auto">
              <Card className="border-0 shadow-sm bg-white">
                <CardContent className="p-8 text-center">
                  <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-5">
                    <Search className="h-7 w-7 text-blue-600" />
                  </div>
                  <h3 className="font-bold text-lg mb-2 text-slate-800">Real-time Updates</h3>
                  <p className="text-slate-600">Track your shipment with live location updates from anywhere.</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm bg-white">
                <CardContent className="p-8 text-center">
                  <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-5">
                    <Ship className="h-7 w-7 text-blue-600" />
                  </div>
                  <h3 className="font-bold text-lg mb-2 text-slate-800">Vessel Information</h3>
                  <p className="text-slate-600">View detailed vessel tracking and route mapping globally.</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm bg-white">
                <CardContent className="p-8 text-center">
                  <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-5">
                    <Calendar className="h-7 w-7 text-blue-600" />
                  </div>
                  <h3 className="font-bold text-lg mb-2 text-slate-800">ETA Notifications</h3>
                  <p className="text-slate-600">Get notified about accurate arrival times and any route delays.</p>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>

      <Footer />
      <QuoteModal open={quoteModalOpen} onOpenChange={setQuoteModalOpen} />
    </div>
  );
};

export default Tracking;
